import type { Applicability, Engine, Values } from '../types';
import { buildUrl } from '../url';
import { distanceParam, priceParam } from './shared';

const price = (values: Values) =>
  typeof values.price === 'object' ? values.price : {};
const sort = (
  options: Array<
    [value: string, token: string, label: string, limit?: Applicability]
  >
) => ({
  id: 'sort',
  type: 'choice' as const,
  label: 'Sort',
  group: 'Sort',
  options: options.map(([value, token, label, limit]) => ({
    value,
    token,
    label,
    ...limit,
  })),
});

const notShipped = {
  when: { param: 'shipping', unset: true as const },
  whenLabel: 'doesn’t apply to items that ship nationwide',
};

export const mercari: Engine = {
  id: 'mercari',
  name: 'Mercari',
  bang: 'merc',
  aliases: ['mercari'],
  homepage: 'https://www.mercari.com',
  category: 'Shopping',
  description:
    'Secondhand listings by condition, price and shipping, including sold prices.',
  params: [
    priceParam,
    {
      id: 'condition',
      type: 'choice',
      label: 'Condition',
      group: 'Listing',
      options: [
        { value: '1', token: 'new', label: 'New' },
        { value: '1-2', token: 'likenew', label: 'Like new or better' },
        { value: '1-2-3', token: 'good', label: 'Good or better' },
      ],
    },
    {
      id: 'freeShipping',
      type: 'flag',
      token: 'fs',
      label: 'Free shipping',
      group: 'Listing',
    },
    {
      id: 'sold',
      type: 'flag',
      token: 'sold',
      label: 'Sold listings',
      group: 'Listing',
    },
    sort([
      ['2', 'newest', 'Newest'],
      ['3', 'low', 'Price: low to high'],
      ['4', 'high', 'Price: high to low'],
      [
        '6',
        'recentsold',
        'Recently sold',
        {
          when: { param: 'sold', in: ['true'] },
          whenLabel: 'only applies with sold listings',
        },
      ],
    ]),
  ],
  build({ query, values }) {
    const p = price(values);
    return buildUrl('https://www.mercari.com/search/', {
      keyword: query,
      minPrice: p.min !== undefined ? p.min * 100 : undefined,
      maxPrice: p.max !== undefined ? p.max * 100 : undefined,
      itemConditions: values.condition as string | undefined,
      shippingPayerIds: values.freeShipping === true && '2',
      itemStatuses: values.sold === true && '2',
      sortBy: values.sort as string | undefined,
    });
  },
};

export const offerup: Engine = {
  id: 'offerup',
  name: 'OfferUp',
  bang: 'ou',
  aliases: ['offerup'],
  homepage: 'https://offerup.com',
  category: 'Shopping',
  description:
    'Local and shipped listings. Location comes from OfferUp itself.',
  params: [
    priceParam,
    {
      id: 'condition',
      type: 'choice',
      label: 'Condition',
      group: 'Listing',
      options: [
        { value: 'NEW', token: 'new', label: 'New' },
        { value: 'OPEN_BOX', token: 'openbox', label: 'Open box' },
        { value: 'REFURBISHED', token: 'refurb', label: 'Refurbished' },
        { value: 'USED', token: 'used', label: 'Used' },
      ],
    },
    {
      id: 'shipping',
      type: 'flag',
      token: 'ship',
      label: 'Ships nationwide',
      group: 'Listing',
    },
    {
      ...distanceParam('Around the location OfferUp has for you.'),
      ...notShipped,
    },
    sort([
      ['-posted', 'newest', 'Newest'],
      ['price', 'low', 'Price: low to high'],
      ['-price', 'high', 'Price: high to low'],
      ['distance', 'closest', 'Closest', notShipped],
    ]),
  ],
  build({ query, values }) {
    const p = price(values);
    return buildUrl('https://offerup.com/search', {
      q: query,
      price_min: p.min,
      price_max: p.max,
      condition: values.condition as string | undefined,
      delivery_param: values.shipping === true && 's',
      radius: values.distance as number | undefined,
      sort: values.sort as string | undefined,
    });
  },
};

export const etsy: Engine = {
  id: 'etsy',
  name: 'Etsy',
  bang: 'etsy',
  aliases: [],
  homepage: 'https://www.etsy.com',
  category: 'Shopping',
  experimental: true,
  description: 'Handmade and vintage goods.',
  params: [
    priceParam,
    {
      id: 'freeShipping',
      type: 'flag',
      token: 'fs',
      label: 'Free shipping',
      group: 'Filters',
    },
    {
      id: 'sale',
      type: 'flag',
      token: 'sale',
      label: 'On sale',
      group: 'Filters',
    },
    {
      id: 'vintage',
      type: 'flag',
      token: 'vintage',
      label: 'Vintage',
      group: 'Filters',
    },
    {
      id: 'handmade',
      type: 'flag',
      token: 'handmade',
      label: 'Handmade',
      group: 'Filters',
    },
    sort([
      ['price_asc', 'low', 'Price: low to high'],
      ['price_desc', 'high', 'Price: high to low'],
      ['highest_reviews', 'rated', 'Top customer reviews'],
      ['date_desc', 'newest', 'Most recent'],
    ]),
  ],
  build({ query, values }) {
    const p = price(values);
    return buildUrl('https://www.etsy.com/search', {
      q: query,
      min: p.min,
      max: p.max,
      explicit: p.min !== undefined || p.max !== undefined ? '1' : undefined,
      free_shipping: values.freeShipping === true && 'true',
      is_discounted: values.sale === true && 'true',
      is_vintage: values.vintage === true && 'true',
      is_handmade: values.handmade === true && 'true',
      order: values.sort as string | undefined,
    });
  },
};

export const reverb: Engine = {
  id: 'reverb',
  name: 'Reverb',
  bang: 'reverb',
  aliases: ['rv'],
  homepage: 'https://reverb.com',
  category: 'Shopping',
  description: 'Music gear, new and used, including sold prices.',
  params: [
    priceParam,
    {
      id: 'condition',
      type: 'choice',
      label: 'Condition',
      group: 'Listing',
      options: [
        { value: 'new', token: 'new', label: 'New' },
        { value: 'used', token: 'used', label: 'Used' },
        { value: 'b-stock', token: 'bstock', label: 'B-stock' },
      ],
    },
    {
      id: 'freeShipping',
      type: 'flag',
      token: 'fs',
      label: 'Free shipping',
      group: 'Listing',
    },
    {
      id: 'offers',
      type: 'flag',
      token: 'bo',
      label: 'Accepts offers',
      group: 'Listing',
    },
    {
      id: 'local',
      type: 'flag',
      token: 'local',
      label: 'Local pickup',
      group: 'Listing',
    },
    {
      id: 'sold',
      type: 'flag',
      token: 'sold',
      label: 'Sold listings',
      group: 'Listing',
    },
    sort([
      ['price_with_sale|asc', 'low', 'Price: low to high'],
      ['price_with_sale|desc', 'high', 'Price: high to low'],
      ['published_at|desc', 'newest', 'Newest'],
    ]),
  ],
  build({ query, values }) {
    const p = price(values);
    return buildUrl('https://reverb.com/marketplace', {
      query,
      price_min: p.min,
      price_max: p.max,
      condition: values.condition as string | undefined,
      free_shipping: values.freeShipping === true && 'true',
      accepts_offers: values.offers === true && 'true',
      local_pickup: values.local === true && 'true',
      show_only_sold: values.sold === true && 'true',
      sort: values.sort as string | undefined,
    });
  },
};

const shopOnly = {
  when: { param: 'database', unset: true as const },
  whenLabel: 'only applies to the shop, not the catalog',
};

export const discogs: Engine = {
  id: 'discogs',
  name: 'Discogs',
  bang: 'discogs',
  aliases: ['dc'],
  homepage: 'https://www.discogs.com',
  category: 'Shopping',
  description:
    'Records for sale by format and condition; db searches the catalog instead.',
  params: [
    {
      id: 'database',
      type: 'flag',
      token: 'db',
      label: 'Search the catalog, not the shop',
      group: 'Scope',
    },
    {
      id: 'format',
      type: 'choice',
      label: 'Format',
      group: 'Filters',
      options: [
        { value: 'Vinyl', token: 'vinyl', label: 'Vinyl' },
        { value: 'CD', token: 'cd', label: 'CD' },
        { value: 'Cassette', token: 'cassette', label: 'Cassette' },
      ],
    },
    {
      id: 'condition',
      when: { param: 'database', unset: true },
      whenLabel: 'only applies to the shop, not the catalog',
      type: 'choice',
      label: 'Media condition',
      group: 'Filters',
      help: 'Shop only.',
      options: [
        { value: 'Mint (M)', token: 'mint', label: 'Mint' },
        { value: 'Near Mint (NM or M-)', token: 'nm', label: 'Near mint' },
        {
          value: 'Very Good Plus (VG+)',
          token: 'vgplus',
          label: 'Very good plus',
        },
      ],
    },
    { ...priceParam, help: 'Shop only.', ...shopOnly },
    {
      id: 'usSeller',
      when: { param: 'database', unset: true },
      whenLabel: 'only applies to the shop, not the catalog',
      type: 'flag',
      token: 'us',
      label: 'Ships from the US',
      group: 'Filters',
    },
    {
      ...sort([
        ['date-listed,descending', 'newest', 'Newest listings'],
        ['price,ascending', 'low', 'Price: low to high'],
        ['price,descending', 'high', 'Price: high to low'],
      ]),
      ...shopOnly,
    },
  ],
  build({ query, values }) {
    if (values.database === true) {
      return buildUrl('https://www.discogs.com/search', {
        q: query,
        type: 'all',
        format: values.format as string | undefined,
      });
    }
    const p = price(values);
    const [sortBy, order] =
      typeof values.sort === 'string' ? values.sort.split(',') : [];
    return buildUrl('https://www.discogs.com/shop/list', {
      q: query,
      formatName: values.format as string | undefined,
      mediaCondition: values.condition as string | undefined,
      price:
        p.min !== undefined || p.max !== undefined
          ? `${p.min ?? 0}-${p.max ?? ''}`
          : undefined,
      currency: p.min !== undefined || p.max !== undefined ? 'USD' : undefined,
      shipsFrom: values.usSeller === true && 'United States',
      sort: sortBy,
      sortOrder: order,
    });
  },
};

export const swappa: Engine = {
  id: 'swappa',
  name: 'Swappa',
  bang: 'swappa',
  aliases: ['swap'],
  homepage: 'https://swappa.com',
  category: 'Shopping',
  experimental: true,
  description:
    'Used phones and tech. Filters jump to the device page guessed from your query (apple iphone 15).',
  params: [
    {
      id: 'condition',
      type: 'choice',
      label: 'Condition',
      group: 'Listing',
      options: [
        { value: 'new', token: 'new', label: 'New' },
        { value: 'mint', token: 'mint', label: 'Mint' },
        { value: 'good', token: 'good', label: 'Good' },
      ],
    },
    {
      id: 'unlocked',
      type: 'flag',
      token: 'unlocked',
      label: 'Unlocked',
      group: 'Listing',
    },
    sort([
      ['price_low', 'low', 'Price: low to high'],
      ['listing_created_newest', 'newest', 'Newest'],
    ]),
  ],
  build({ query, values }) {
    const filtered = values.condition || values.unlocked || values.sort;
    if (!filtered) return buildUrl('https://swappa.com/search', { q: query });
    const slug = query
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return buildUrl(`https://swappa.com/listings/${slug}`, {
      condition: values.condition as string | undefined,
      carrier: values.unlocked === true && 'unlocked',
      sort: values.sort as string | undefined,
    });
  },
};

const DISTANCES = [10, 20, 30, 40, 50, 75, 100, 150, 200, 250, 500, 750, 1000];

// Makes Cars.com writes as two words (its slugs join them with `_`).
const TWO_WORD_MAKES = new Set([
  'alfa romeo',
  'aston martin',
  'land rover',
  'mercedes benz',
  'rolls royce',
]);

/**
 * Cars.com slugs: `land rover defender` -> land_rover / defender,
 * `jeep grand cherokee` -> jeep / grand_cherokee, `c-class` -> c_class.
 */
function carsMakeModel(query: string): {
  make?: string;
  model?: string;
} {
  const words = query
    .toLowerCase()
    .replace(/-/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const two = words.slice(0, 2).join(' ');
  const makeWords = TWO_WORD_MAKES.has(two) ? 2 : 1;
  const make = words.slice(0, makeWords).join('_') || undefined;
  const model = words.slice(makeWords).join('_') || undefined;
  return { make, model };
}

export const carscom: Engine = {
  id: 'carscom',
  name: 'Cars.com',
  bang: 'cars',
  aliases: ['carscom'],
  homepage: 'https://www.cars.com',
  category: 'Shopping',
  description:
    'Cars by make and model (query: toyota tacoma), year, price, mileage and distance.',
  params: [
    {
      id: 'stock',
      type: 'choice',
      label: 'Stock',
      group: 'Listing',
      options: [
        { value: 'used', token: 'used', label: 'Used' },
        { value: 'new', token: 'new', label: 'New' },
        { value: 'cpo', token: 'cpo', label: 'Certified pre-owned' },
      ],
    },
    {
      id: 'years',
      type: 'range',
      prefix: 'y',
      label: 'Model years',
      group: 'Listing',
      help: 'y2018-2022, y2020-.',
    },
    priceParam,
    {
      id: 'mileage',
      when: { param: 'stock', in: ['used', 'cpo'], orUnset: true },
      whenLabel: 'doesn’t apply to new cars',
      type: 'number',
      suffix: 'k',
      label: 'Max mileage (thousands)',
      group: 'Listing',
      placeholder: '80',
      help: '80k = under 80,000 miles.',
    },
    distanceParam(
      'From the zip in Settings; rounds up to the site’s distance steps.'
    ),
    sort([
      ['list_price', 'low', 'Price: low to high'],
      ['list_price_desc', 'high', 'Price: high to low'],
      ['listed_at_desc', 'newest', 'Newest listings'],
      ['mileage', 'miles', 'Lowest mileage'],
      ['year_desc', 'year', 'Newest year'],
      ['distance', 'closest', 'Closest'],
    ]),
  ],
  build({ query, values, settings }) {
    const { make, model } = carsMakeModel(query);
    const p = price(values);
    const years = typeof values.years === 'object' ? values.years : {};
    const distance =
      typeof values.distance === 'number'
        ? (DISTANCES.find(d => d >= (values.distance as number)) ?? 9999)
        : undefined;
    const url = new URL('https://www.cars.com/shopping/results/');
    url.searchParams.set(
      'stock_type',
      (values.stock as string | undefined) ?? 'all'
    );
    if (make) url.searchParams.append('makes[]', make);
    if (make && model) url.searchParams.append('models[]', `${make}-${model}`);
    const set = (k: string, v: number | string | undefined) => {
      if (v !== undefined) url.searchParams.set(k, String(v));
    };
    set('year_min', years.min);
    set('year_max', years.max);
    set('list_price_min', p.min);
    set('list_price_max', p.max);
    set(
      'mileage_max',
      typeof values.mileage === 'number' ? values.mileage * 1000 : undefined
    );
    set('zip', settings.zip);
    set('maximum_distance', distance);
    set('sort', values.sort as string | undefined);
    return url.toString();
  },
  example: { query: 'toyota tacoma', mods: [] },
};

export const bat: Engine = {
  id: 'bat',
  name: 'Bring a Trailer',
  bang: 'bat',
  aliases: ['bringatrailer'],
  homepage: 'https://bringatrailer.com',
  category: 'Shopping',
  description: 'Live and past collector-car auctions, including sold prices.',
  params: [
    {
      id: 'result',
      type: 'choice',
      label: 'Past auctions',
      group: 'Auctions',
      help: 'Leave off for live auctions.',
      options: [
        { value: 'sold', token: 'sold', label: 'Sold' },
        { value: 'unsold', token: 'unsold', label: 'Reserve not met' },
        { value: 'all', token: 'past', label: 'All past' },
      ],
    },
    {
      id: 'years',
      type: 'range',
      prefix: 'y',
      label: 'Model years',
      group: 'Auctions',
      help: 'y1965-1973.',
    },
    {
      id: 'noReserve',
      type: 'flag',
      token: 'noreserve',
      label: 'No reserve',
      group: 'Auctions',
    },
    sort([['bd', 'high', 'Highest bid']]),
  ],
  build({ query, values }) {
    const past = typeof values.result === 'string';
    const years = typeof values.years === 'object' ? values.years : {};
    return buildUrl(
      past
        ? 'https://bringatrailer.com/auctions/results/'
        : 'https://bringatrailer.com/auctions/',
      {
        search: query,
        result:
          past && values.result !== 'all'
            ? (values.result as string)
            : undefined,
        yearFrom: years.min,
        yearTo: years.max,
        type: values.noReserve === true && 'noreserve',
        sortby: values.sort as string | undefined,
      }
    );
  },
};
